<script lang="ts" setup>
  import { computed, ref, onMounted } from 'vue';
  import { useCart, useBreakpoints, useOffersPermissions, useOrdersPremissions, viewCartEvent, beginCheckoutEvent, useGoogleTagManager } from '@storefront/storefront';
  import { useAuthenticated, type FormContextInterface, setModelWithContext, useShared } from '@othercommerce/foundation';
  import { Features } from '@shared/features';
  import type { ProductVariantSimpleResource } from '@storefront/resources';

  interface Props {
    related: ProductVariantSimpleResource[],
    enableLoyalty: boolean,
    freeDeliveryRemaining: number | null | string,
    discountsDebugLog: string | null,
  }

  defineProps<Props>();

  const { items, count, cart } = useCart();
  const { canRequestOffers } = useOffersPermissions();
  const { canPlaceOrders } = useOrdersPremissions();

  const authenticated = useAuthenticated();
  const breakpoints = useBreakpoints();
  const shared = useShared();
  const gtm = useGoogleTagManager();

  const active = ref();
  const displayDiscountLogs = ref(false);

  const empty = computed(() => {
    return count.value === 0;
  });

  const parameters = ref({
    breakpoints: {
      1: {
        slidesPerView: 1.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      576: {
        slidesPerView: 2.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      992: {
        slidesPerView: 3.25,
        spaceBetween: 10,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
    navigation: true,
  });

  function toggle(section: string) {
    if (active.value !== section) {
      active.value = section;
    } else {
      active.value = null;
    }
  }

  function onOrderSubmit(ctx: FormContextInterface, submit: () => void) {
    setModelWithContext('mode', ctx, 'order');
    gtm.push(beginCheckoutEvent(cart.value));
    submit();
  }

  function onOfferSubmit(ctx: FormContextInterface, submit: () => void) {
    setModelWithContext('mode', ctx, 'offer');
    submit();
  }

  onMounted(() => {
    gtm.push(viewCartEvent(cart.value, shared.value.activeCurrencyCode));
  });
</script>
<template>
  <main id="main" class="order order-cart">
    <FreeDeliveryRemaining :freeDeliveryRemaining />
    <div class="order-cart__content">
      <div class="order-cart__column">
        <div class="order-cart__column-products">
          <template v-if="authenticated">
            <div v-if="authenticated.creditLimit && authenticated.credit >= authenticated.creditLimit" class="order-cart-credit-warning">
              <p>{{ $t('SHOP.CART.WARNING.CREDIT_LIMIT_EXCEEDED') }}</p>
            </div>
            <div v-if="authenticated.creditOverdueLimit && authenticated.creditOverdue >= authenticated.creditOverdueLimit" class="order-cart-credit-warning">
              <p>{{ $t('SHOP.CART.WARNING.CREDIT_OVERDUE_LIMIT_EXCEEDED') }}</p>
            </div>
            <div v-if="authenticated.creditDocumentsLimit && authenticated.creditDocuments >= authenticated.creditDocumentsLimit" class="order-cart-credit-warning">
              <p>{{ $t('SHOP.CART.WARNING.CREDIT_DOCUMENTS_LIMIT_EXCEEDED') }}</p>
            </div>
            <div v-if="authenticated.paymentsOverdueDaysLimit && authenticated.paymentsOverdueDays >= authenticated.paymentsOverdueDaysLimit" class="order-cart-credit-warning">
              <p>{{ $t('SHOP.CART.WARNING.PAYMENTS_OVERDUE_DAYS_LIMIT_EXCEEDED') }}</p>
            </div>
          </template>
          <SfTile>
            <template #outer>
              <div class="order-cart__header">
                <h1 class="order-heading">
                  {{ $t('SHOP.CART.HEADER.CART') }}
                </h1>
                <div class="order-cart__count">
                  {{ $tc('SHOP.CART.HEADER.PRODUCTS_COUNT', count, { count }) }}
                </div>
              </div>
              <div class="order-cart__buttons">
                <CartEmptyButton :cart :isCartEmpty="empty" />
                <CartWishlistButton :isCartEmpty="empty" />
                <SfButton :href="$route('web.cart.import.create')" small transparent>
                  <template #before>
                    <SfIconBoxArrowInDown aria-hidden="true" />
                  </template>
                  <template #default>
                    {{ $t('SHOP.CART.ACTION.IMPORT_FROM_FILE') }}
                  </template>
                </SfButton>
                <a :href="$route('web.cart.export')" class="btn btn--small btn--transparent" download>
                  <span class="btn__before">
                    <SfIconBoxArrowInUp aria-hidden="true" />
                  </span>
                  <span class="btn__inner">
                    {{ $t('SHOP.CART.ACTION.EXPORT_TO_FILE') }}
                  </span>
                </a>
              </div>
            </template>
            <template #default>
              <div v-if="!empty" class="order-cart__products">
                <CartItem v-for="item in items" :key="item.id" :item="item" />
              </div>
              <div v-else class="order-cart__empty">
                <SfDataPlaceholder>
                  {{ $t('SHOP.CART.MESSAGE.NO_PRODUCTS_IN_CART') }}
                </SfDataPlaceholder>
              </div>
            </template>
          </SfTile>
          <div class="order-cart__warning">
            <SfIconInfo aria-hidden="true" />
            {{ $t('SHOP.CART.MESSAGE.DO_NOT_WAIT_WITH_PURCHASE') }}
          </div>
        </div>
        <SfTile v-if="related.length > 0" class="order-cart__related-tile">
          <template #outer>
            <h2 class="order-cart__related-header">
              {{ $t('SHOP.CART.HEADER.MAY_BE_INTERESTED') }}
            </h2>
          </template>
          <SfProductsCarousel :parameters :products="related" class="order-cart__related" />
        </SfTile>
      </div>
      <div class="order-cart__column order-cart__column--right">
        <div class="order-cart__summary">
          <SfTile>
            <template v-if="breakpoints.xl" #outer>
              <h2 :aria-hidden="!breakpoints.xl" :class="{ 'hide': !breakpoints.xl }" class="order-heading">
                {{ $t('SHOP.CART.HEADER.SUMMARY') }}
              </h2>
            </template>
            <template #default>
              <OrderSummary :order="cart">
                <div class="order-cart__actions">
                  <FormController v-slot="{ processing, ctx, submit }" :action="$route('web.cart.update')" method="PATCH">
                    <SfButton v-if="canPlaceOrders" :disabled="empty" :loading="processing" primary @click="onOrderSubmit(ctx, submit)">
                      {{ $t('SHOP.CART.ACTION.CHECKOUT') }}
                    </SfButton>
                    <SfButton v-if="canRequestOffers" :disabled="empty" :loading="processing" @click="onOfferSubmit(ctx, submit)">
                      {{ $t('SHOP.CART.ACTION.OFFER') }}
                    </SfButton>
                  </FormController>
                </div>
                <div class="order-cart__discount">
                  <CartLoyaltyPoints v-if="authenticated && Features.loyaltyPoints.isEnabled && enableLoyalty" :open="active === 'loyalty'" @toggle="toggle" />
                  <CartDiscountCode :open="active === 'discount'" @toggle="toggle" />

                  <template v-if="discountsDebugLog">
                    <SfButton small @click="displayDiscountLogs = true">
                      🧰 Zobacz logi rabatów
                    </SfButton>
                    <UiDrawer v-model:open="displayDiscountLogs" lg>
                      <div class="discounts-log" v-html="discountsDebugLog" />
                      <UiButtonGroup>
                        <SfButton @click="displayDiscountLogs = false">Zamknij</SfButton>
                      </UiButtonGroup>
                    </UiDrawer>
                  </template>
                </div>
              </OrderSummary>
              <div class="order-cart__warning">
                <SfIconInfo aria-hidden="true" />
                {{ $t('SHOP.CART.MESSAGE.DO_NOT_WAIT_WITH_PURCHASE') }}
              </div>
            </template>
          </SfTile>
          <div v-if="authenticated && authenticated.b2b" class="customer-credit">
            <h2>{{ $t('SHOP.CART.HEADER.CUSTOMER_CREDIT') }}</h2>
            <dl>
              <dt>
                {{ $t('SHOP.CART.LABEL.CREDIT') }}
              </dt>
              <dd>
                <SfMoney :value="authenticated.credit" />
              </dd>
              <dt v-if="authenticated.creditLimit">
                {{ $t('SHOP.CART.LABEL.CREDIT_LIMIT') }}
              </dt>
              <dd v-if="authenticated.creditLimit">
                <SfMoney :value="authenticated.creditLimit" />
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  </main>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .discounts-log {
    font-size: 12px;

    table {
      margin: 4px 6px;
      border-spacing: 0;
      border-collapse: collapse;
      font-size: 12px;

      tr {
        td, th {
          padding: 2px 4px;
          border: 1px solid gray;
        }
      }
    }

    ul {
      padding: 8px 16px;
      list-style: none;
    }

    p {
      padding: 0 12px;
      font-size: 12px;
    }

    b {
      font-weight: 900;
    }

    details {
      padding: 9px 13px;
      margin: 0;
      border: 1px solid #e0e0e0;

      summary {
        background: #eeeeee;
        border: 1px solid #e0e0e0;
        padding: 4px 6px;
        font-size: 12px;
        margin: -8px -12px -8px -12px;
      }

      &[open] {
        & > summary {
          margin-bottom: 9px;
        }
      }

      p {
        font-size: 12px;
      }
    }

    details ~ p {
      padding-top: 9px;
    }

    p + details {
      margin-top: 9px;
    }
  }

  .order-cart {
    --sf-data-placeholder-font-size: 1.25rem;
    --sf-data-placeholder-font-weight: var(--font-weight-semi);
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-width: var(--sf-order-cart-max-width);
    min-height: unset;

    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2rem;
      padding: 4rem 0;
    }

    &__header {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }

    &__content {
      display: grid;
      grid-template-columns: 3fr minmax(400px, 1fr);
      gap: 2rem;
    }

    &__column {
      display: flex;
      flex-direction: column;
      gap: var(--sf-section-renderer-gap, 2rem);
      // For Swiper - https://github.com/nolimits4web/swiper/issues/2914#issuecomment-570503437
      overflow: hidden;

      &--right {
        overflow: unset;
      }
    }

    &__column-products {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .tile__outer {
        flex-wrap: wrap;
      }
    }

    &__products {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__count {
      display: inline-block;
      font-size: 0.875rem;
      font-weight: var(--font-weight-semi);
      padding: 0.25rem 0.5rem;
      background-color: var(--sf-order-cart-count-background);
      border-radius: var(--sf-order-cart-count-border-radius);
      white-space: nowrap;
    }

    &__buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem 0.875rem;
      padding-top: 0.25rem;
    }

    &__warning {
      display: flex;
      column-gap: 0.5rem;
      align-items: center;
      font-size: 0.875rem;
      color: var(--sf-order-cart-warning-color);
      text-wrap: balance;
    }

    &__summary {
      position: sticky;
      top: var(--sf-order-cart-summary-top);

      .customer-credit {
        font-size: 0.875rem;
        border: 1px solid transparent;
        padding: 2rem;

        h2 {
          font-size: 1.25rem;
          margin-bottom: 1rem;
        }

        dl {
          display: grid;
          grid-template-columns: auto 1fr;
          column-gap: 1rem;
          row-gap: 0.25rem;

          dd {
            font-weight: bold;
          }
        }
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__discount {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin: 2rem 0 0.5rem;
    }

    &__column--right .order-cart__warning {
      display: none
    }
  }

  @include media.query(xl) {
    .order-cart {
      &__content {
        grid-template-columns: 1fr;
        gap: 1rem;
        width: 100%;
      }

      &__warning {
        display: none;
      }

      &__column--right .order-cart__warning {
        display: flex;
        align-items: flex-start;

        svg {
          margin-top: 0.125rem;
        }
      }

      &__products {
        gap: 0.75rem;
      }

      &__buttons {
        width: 100%;
      }
    }
  }

  @include media.query(lg) {
    .order-cart {
      gap: 1rem;
    }
    .order-cart__related-tile .tile__content {
      padding-left: 0;
      padding-right: 0;
    }
  }

  @include media.query(sm) {
    .order-cart__buttons {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
